import { Container } from '@mui/material';
import appLogger from '@shared/logging/appLogger';
import { GetAdobeNameForPath } from '@shared/utilities/helper/adobe';
import urlData from '@shared/utilities/routes';
import { ReactNode } from 'react';

import CarMaxLogo from '../../CarMaxLogo';
import styles from './Footer.module.scss';

interface FooterLink {
    url: string;
    text: string;
    external?: boolean;
    isExtraSmall?: boolean;
    ariaLabel?: string;
}

interface FooterLinkSectionProps {
    title: string;
    children: ReactNode;
}

const Footer: React.FC = () => {
    const Divider = () => <span className={styles['footer-secondary-navigation-divider']}>|</span>;
    const FooterLink = ({ text, url, external, ariaLabel, isExtraSmall = false }: FooterLink) => {
        return (
            <hzn-text-link
                block
                no-underline
                size={isExtraSmall ? 'caption' : 'small'}
                tone="inverse"
                class={styles['footer-link']}
                href={url}
                hzn-aria-label={ariaLabel || ''}
                target={external ? '_blank' : ''}
                rel="noreferrer">
                {text}
            </hzn-text-link>
        );
    };
    const FooterLinkSection = ({ title, children }: FooterLinkSectionProps) => {
        return (
            <section className={styles['footer-primary-navigation-section']}>
                <hzn-text
                    size="base"
                    tone="inverse"
                    class={styles['footer-primary-navigation-section-header']}
                    weight="bold">
                    {title}
                </hzn-text>
                <ul className={styles['footer-primary-navigation-section-list']}>{children}</ul>
            </section>
        );
    };
    return (
        <footer
            data-testid="footer"
            className={styles['footer-bar']}
            onClickCapture={e => {
                const targetElement = e.target as HTMLElement;
                if (['a', 'hzn-text-link'].indexOf(targetElement.tagName.toLowerCase()) >= 0) {
                    appLogger.logEvent({
                        name: 'Click',
                        properties: {
                            linkOriginatingPageName: GetAdobeNameForPath(),
                            linkText: targetElement?.textContent,
                            linkPosition: 'Site Footer',
                        },
                    });
                }
            }}>
            <Container>
                <CarMaxLogo logoType="small" />
                <div className={styles['footer-section-divider']} />
                <div
                    className={styles['footer-primary-navigation']}
                    data-testid="footer-primary-navigation">
                    <FooterLinkSection title={'Auctions'}>
                        <li>
                            <FooterLink
                                text="About Us"
                                url={
                                    urlData.about.shouldUseMFE
                                        ? urlData.about.mfeLink
                                        : urlData.about.legacyLink
                                }
                                ariaLabel={urlData.about.ariaLabel}
                            />
                        </li>
                        <li>
                            <FooterLink
                                text="FAQ"
                                url={
                                    urlData.faq.shouldUseMFE
                                        ? urlData.faq.mfeLink
                                        : urlData.faq.legacyLink
                                }
                                ariaLabel={urlData.faq.ariaLabel}
                            />
                        </li>
                        <li>
                            <FooterLink
                                text="Auction Policies"
                                url={
                                    urlData.policy.shouldUseMFE
                                        ? urlData.policy.mfeLink
                                        : urlData.policy.legacyLink
                                }
                                ariaLabel={urlData.policy.ariaLabel}
                            />
                        </li>
                    </FooterLinkSection>
                    <FooterLinkSection title={'Contact'}>
                        <li>
                            <FooterLink
                                text="Contact Us"
                                url={
                                    urlData.contact.shouldUseMFE
                                        ? urlData.contact.mfeLink
                                        : urlData.contact.legacyLink
                                }
                                ariaLabel={urlData.contact.ariaLabel}
                            />
                        </li>
                        <li>
                            <FooterLink text="carmax.com" url="https://www.carmax.com" />
                        </li>
                    </FooterLinkSection>
                    <FooterLinkSection title={'About'}>
                        <li>
                            <FooterLink
                                text="About CarMax"
                                url="https://www.carmax.com/about-carmax"
                            />
                        </li>
                        <li>
                            <FooterLink
                                text="Social Responsibility"
                                url="https://socialresponsibility.carmax.com/"
                            />
                        </li>
                        <li>
                            <FooterLink
                                text="CarMax Foundation"
                                url="https://foundation.carmax.com"
                            />
                        </li>
                    </FooterLinkSection>
                    <FooterLinkSection title={'Finance'}>
                        <li>
                            <FooterLink
                                text="AFC Financing"
                                url="https://www.autofinance.com"
                                ariaLabel='Opens in a new window. "AFC Financing"'
                                external
                            />
                        </li>
                        <li>
                            <FooterLink
                                text="NextGear Financing"
                                url="https://www.nextgearcapital.com"
                                ariaLabel='Opens in a new window. "NextGear Financing"'
                                external
                            />
                        </li>
                        <li>
                            <FooterLink
                                text="Kinetic Advantage"
                                url="https://www.kineticadvantage.com/carmax/"
                                ariaLabel='Opens in a new window. "Kinetic Advantage"'
                                external
                            />
                        </li>
                        <li>
                            <FooterLink
                                text="Westlake Flooring"
                                url="https://signup.westlakefinancial.com/?src=carmax"
                                ariaLabel='Opens in a new window. "Westlake Flooring"'
                                external
                            />
                        </li>
                    </FooterLinkSection>
                    <FooterLinkSection title={'Locations'}>
                        <li>
                            <FooterLink
                                text="Locations & Schedules"
                                url={
                                    urlData.locations.shouldUseMFE
                                        ? urlData.locations.mfeLink
                                        : urlData.locations.legacyLink
                                }
                                ariaLabel={urlData.locations.ariaLabel}
                            />
                        </li>
                    </FooterLinkSection>
                </div>
                <section
                    className={styles['footer-secondary-navigation']}
                    data-testid="footer-secondary-navigation">
                    <FooterLink
                        isExtraSmall={true}
                        text="Privacy Policy"
                        url="https://www.carmax.com/privacy-policy"
                    />
                    <Divider />
                    <FooterLink
                        isExtraSmall={true}
                        text="Do Not Sell or Share My Information"
                        url="https://www.carmax.com/privacy-policy/opt-out"
                    />
                    <Divider />
                    <FooterLink
                        isExtraSmall={true}
                        text="Financial Privacy Policy"
                        url="https://www.carmax.com/pre-qual/files/financial-privacy-policy-feb-2016.pdf"
                    />
                    <Divider />
                    <FooterLink
                        isExtraSmall={true}
                        text="Interest-Based Ads"
                        url="https://www.carmax.com/interest-based-ads"
                    />
                    <Divider />
                    <FooterLink
                        isExtraSmall={true}
                        text="Terms of Use"
                        url="https://www.carmax.com/terms"
                    />
                    <Divider />
                    <FooterLink
                        isExtraSmall={true}
                        text="CarMax Recall Policy"
                        url="https://www.carmax.com/faq/safety-recalls"
                    />
                    <Divider />
                    <FooterLink
                        isExtraSmall={true}
                        text="CA Supply Chain Transparency"
                        url="https://www.carmax.com/terms#ca-disclosure"
                    />
                    <Divider />
                    <FooterLink
                        isExtraSmall={true}
                        text="Accessibility"
                        url="https://www.carmax.com/notice-of-accessibility"
                    />
                </section>
                <section className={styles['footer-copyright']} data-testid="footer-copyright">
                    <hzn-text variant="caption" tone="inverse" align="left">
                        Copyright © {new Date().getFullYear()} CarMax Enterprise Services, LLC
                    </hzn-text>
                </section>
            </Container>
        </footer>
    );
};

export default Footer;
